import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import posthog from 'posthog-js';
import { isPlatform } from '@ionic/react';
import { PushNotifications } from '@capacitor/push-notifications';
import api from '../../api/apiHelper';
import { invalidateApiCache } from '../../utils/apiCache';
import { registerForPushNotifications, syncPushTokenIfGranted } from '../../services/pushNotifications';
import matematlyLogo from '../../img/logo_full.png';

// Importy ikon
import {
    FiTarget, FiTrendingUp, FiCalendar,
    FiCpu, FiCheck, FiBell
} from 'react-icons/fi';

import {
    HiOutlineBolt, HiOutlineAcademicCap, HiOutlineSparkles,
    HiOutlineShieldCheck, HiOutlineChartBarSquare,
} from 'react-icons/hi2';

// Import z utils
import { getMaturaDate } from '../../utils/configUtils';

// --- WARIANTY ANIMACJI ---
const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

const modalVariants = {
  hidden: { opacity: 0, y: 50, scale: 0.95 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { type: 'spring', bounce: 0, duration: 0.4 } },
  exit: { opacity: 0, y: 20, scale: 0.95, transition: { duration: 0.2 } },
};

const contentVariants = {
  hidden: { opacity: 0, x: 20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, x: -20, transition: { duration: 0.2 } }
};

// --- DANE KONFIGURACYJNE ---
const GOAL_PROFILES = [
    { id: 'pass',  label: 'Zdać na 30%',   short: 'Minimum',  desc: 'Celuję w 30+%',  baseTasks: 3,  targetScore: '30%',  Icon: HiOutlineShieldCheck },
    { id: 'solid', label: 'Zdać solidnie', short: 'Solidnie', desc: 'Celuję w 60+%',  baseTasks: 5,  targetScore: '60%',  Icon: HiOutlineChartBarSquare },
    { id: 'max',   label: 'Zdać na maksa', short: 'Maks',     desc: 'Celuję w 90%+', baseTasks: 10, targetScore: '90%+', Icon: HiOutlineBolt }
];

// Rozszerzenie nie ma progu zdawalnosci — cele to progi rekrutacyjne (zgodnie z ROADMAP_DATA_PR).
const GOAL_PROFILES_PR = [
    { id: 'counts', label: 'Żeby się liczyło', short: 'Liczy się', desc: 'Celuję w 30%+', targetScore: '30%+', Icon: HiOutlineShieldCheck },
    { id: 'good',   label: 'Dobry wynik',   short: 'Dobry',     desc: 'Celuję w 60%+', targetScore: '60%+', Icon: HiOutlineChartBarSquare },
    { id: 'top',    label: 'Super wynik',  short: 'Topowy',    desc: 'Celuję w 80%+', targetScore: '80%+', Icon: HiOutlineBolt }
];

const EXAM_CHOICES = [
    { id: 'PP',    label: 'Tylko podstawę',          Icon: HiOutlineAcademicCap, desc: 'Chcę mieć matmę z głowy' },
    { id: 'PP_PR', label: 'Podstawę i rozszerzenie', Icon: HiOutlineSparkles,    desc: 'Potrzebuję rozszerzenia na studia' },
];


const PROCESSING_MESSAGES = [
    "Obliczanie czasu do matury...",
    "Obliczanie celu dziennego...",
];

// Kroki flow. Po wyborze poziomu idziemy albo w GOAL_PP (tylko podstawa), albo w GOAL_PR (P+R).
const STEP = {
    EXAM: 0,
    GOAL_PP: 1,
    GOAL_PR: 2,
    LOADING: 3,
    PLAN: 4,
    PUSH: 5,
    TRIAL: 6,
};

// --- LOGIKA PLANU ---
const calculatePlan = (goalId, confidenceId, isTopPr) => {
    let strategyName = "Indywidualna Ścieżka";

    if (goalId === 'pass') {
        if (confidenceId === 'low') strategyName = "Operacja: Przetrwanie";
        else strategyName = "Zdać i zapomnieć"; 
    } else if (goalId === 'solid') {
        if (confidenceId === 'low') strategyName = "Krok po kroku";
        else if (confidenceId === 'mid') strategyName = "Solidnie i bez stresu";
        else strategyName = "Optymalizacja Wyniku"; 
    } else if (goalId === 'max') {
        if (confidenceId === 'high') strategyName = "Droga do 100%";
        else strategyName = "Matma to życie"; 
    }

    // Cel dzienny zalezy wylacznie od celu z rozszerzenia: 5 zadan tylko przy "top"
    // (rozszerzenie na maksa), kazdy inny wybor dostaje 3.
    const dailyTasks = isTopPr ? 5 : 3;

    return { strategyName, dailyTasks };
};

const OnboardingPopup = ({ onClose, onGoalUpdate }) => {
  const [step, setStep] = useState(STEP.EXAM);

  const [examChoice, setExamChoice] = useState('PP');
  const [selectedGoalProfile, setSelectedGoalProfile] = useState(GOAL_PROFILES[1]);
  const [selectedGoalProfilePr, setSelectedGoalProfilePr] = useState(GOAL_PROFILES_PR[1]);
  const [finalPlan, setFinalPlan] = useState({ strategyName: '', dailyTasks: 3 });
  const [processingMsgIndex, setProcessingMsgIndex] = useState(0);
  const [countdown, setCountdown] = useState(null);
  
  const maturaDateTarget = useMemo(() => getMaturaDate(), []);

  // Prośba o pushe wraca przed pierwszą sesję: na koniec sesji widzieli ją tylko ci,
  // którzy zrobili cel dzienny. Krok pokazujemy, tylko gdy system pozwala jeszcze zapytać.
  const [canAskPush, setCanAskPush] = useState(false);
  const [hasPushStep, setHasPushStep] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
      posthog.capture('onboarding_started_v5_push'); // Nowa wersja
      // Samouczek to stałe zadanie — pobieramy je w tle, żeby po onboardingu nie było drugiego spinnera.
      api.get("/tutorial-problem/", { skipLoading: true })
          .then((res) => {
              if (res.data && "tutorial" in res.data) sessionStorage.setItem("tutorialPrefetch", JSON.stringify(res.data));
          })
          .catch(() => {});
      if (!isPlatform('capacitor')) return;
      PushNotifications.checkPermissions()
          .then((perm) => {
              const askable = String(perm.receive).startsWith('prompt');
              setCanAskPush(askable);
              setHasPushStep(askable);
              // Zgoda z góry (Android ≤12): cichy sync w App.js leciał przed logowaniem,
              // więc bez tego token trafiłby do bazy dopiero przy kolejnym uruchomieniu.
              if (perm.receive === 'granted') syncPushTokenIfGranted();
          })
          .catch(() => setCanAskPush(false));
  }, []);

  useEffect(() => {
      posthog.capture('onboarding_step_viewed', { 
          step_number: step,
          step_name: getStepName(step)
      });
  }, [step]);

  const isDual = examChoice === 'PP_PR';

  const getStepName = (s) => {
      switch(s) {
          case STEP.EXAM:    return 'exam_choice';
          case STEP.GOAL_PP: return 'goal_selection';
          case STEP.GOAL_PR: return 'goal_selection_pr';
          case STEP.LOADING: return 'loading';
          case STEP.PLAN:    return 'plan_reveal';
          case STEP.PUSH:    return 'push_permission';
          case STEP.TRIAL:   return 'trial_info';
          default: return `step_${s}`;
      }
  };

  const handleExamChoiceSelect = (choiceId) => {
      posthog.capture('onboarding_exam_choice_selected', { exam_choice: choiceId });
      setExamChoice(choiceId);
      // Przy P+R cel z podstawy nie jest wyborem — jedziemy na maksa (pokazujemy to wprost na kolejnym ekranie).
      setSelectedGoalProfile(choiceId === 'PP_PR' ? GOAL_PROFILES[2] : GOAL_PROFILES[1]);
  };

  // Onboarding kończy się popupem "Zaczynamy od podstawy" — tylko dla P+R.
  const finish = () => onClose({ showDomainIntro: isDual });

  const handleNext = async () => {
    if (step === STEP.EXAM) {
        setStep(isDual ? STEP.GOAL_PR : STEP.GOAL_PP);
    } else if (step === STEP.GOAL_PP || step === STEP.GOAL_PR) {
        startProcessing();
    } else if (step === STEP.PLAN) {
        setStep(canAskPush ? STEP.PUSH : STEP.TRIAL);
    } else if (step === STEP.PUSH) {
        await handlePushAccept();
    } else if (step === STEP.TRIAL) {
        finish();
    } else {
        setStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
      if (step === STEP.GOAL_PR) {
          setStep(STEP.EXAM);
      } else if (step === STEP.PLAN) {
          setStep(isDual ? STEP.GOAL_PR : STEP.GOAL_PP);
      } else if (step === STEP.PUSH) {
          setStep(STEP.PLAN);
      } else if (step === STEP.TRIAL) {
          setStep(canAskPush ? STEP.PUSH : STEP.PLAN);
      } else if (step > STEP.EXAM) {
          setStep(prev => prev - 1);
      }
  };

  // Te same zdarzenia co prośba na koniec sesji, z context/flow = onboarding.
  const handlePushAccept = async () => {
      if (pushBusy) return;
      setPushBusy(true);
      posthog.capture('push_onboarding_modal_clicked', { action: 'accepted', context: 'onboarding' });
      try {
          await registerForPushNotifications();
          const finalCheck = await PushNotifications.checkPermissions();
          posthog.capture('push_native_permission_result', { status: finalCheck.receive, flow: 'onboarding' });
          setCanAskPush(false);
      } catch (e) {
          posthog.capture('push_error', { error: e?.message, flow: 'onboarding' });
      } finally {
          setPushBusy(false);
          setStep(STEP.TRIAL);
      }
  };

  // Odmowa jak na koniec sesji: druga szansa po 12h (EndSessionStats), krok zostaje,
  // żeby "Wstecz" z ekranu triala dalej działało.
  const handlePushDecline = () => {
      posthog.capture('push_onboarding_modal_clicked', { action: 'declined', context: 'onboarding' });
      localStorage.setItem('push_prompt_dismissed', Date.now().toString());
      setStep(STEP.TRIAL);
  };

  const startProcessing = () => {
      setStep(STEP.LOADING);
      setProcessingMsgIndex(0);

      const isTopPr = isDual && selectedGoalProfilePr.id === 'top';
      const calculated = calculatePlan(selectedGoalProfile.id, 'mid', isTopPr);
      setFinalPlan(calculated);

      posthog.capture('onboarding_plan_generated', {
          exam_choice: examChoice,
          chosen_goal: selectedGoalProfile.id,
          chosen_goal_pr: selectedGoalProfilePr.id,
          generated_daily_tasks: calculated.dailyTasks,
          generated_strategy: calculated.strategyName
      });

      saveOnboarding(calculated.dailyTasks, calculated.strategyName);

      let msgIndex = 0;
      const interval = setInterval(() => {
          msgIndex++;
          if (msgIndex < PROCESSING_MESSAGES.length) {
              setProcessingMsgIndex(msgIndex);
          } else {
              clearInterval(interval);
              setStep(STEP.PLAN);
          }
      }, 600);
  };

  // Jeden strzal na koniec kroku "personalizacja": deklaracja poziomu, cele obu torow,
  // cel dzienny i strategia. Backend przy PP_PR przygotowuje tez dzialy toru PR.
  const saveOnboarding = (tasksCount, strategyName) => {
      localStorage.setItem('user_current_roadmap_goal', selectedGoalProfile.id);
      if (onGoalUpdate) onGoalUpdate(selectedGoalProfile.id, tasksCount);

      // goal_id_pr leci zawsze — przy "tylko podstawa" jest to domyslny sredni cel,
      // zapisywany po cichu, bo tego ekranu user nie widzial.
      const payload = {
          exam_choice: examChoice,
          goal_id: selectedGoalProfile.id,
          goal_id_pr: selectedGoalProfilePr.id,
          daily_goal: tasksCount,
          strategy_name: strategyName,
      };

      api.post('complete-onboarding/', payload, { skipLoading: true })
        .then(() => {
            const currentStatusRaw = localStorage.getItem("userStatus");
            const currentStatus = currentStatusRaw ? JSON.parse(currentStatusRaw) : {
                problems_solved_today: 0,
                current_streak: 0,
                problems_solved_total: 0,
            };
            currentStatus.problems_daily_goal = tasksCount;
            currentStatus.declares_pr = isDual;
            localStorage.setItem("userStatus", JSON.stringify(currentStatus));

            // Backend mogl ruszyc selected_fields / UserElo dla PR — niech ekrany
            // domenowe dociagna swieze dane zamiast jechac z cache sprzed onboardingu.
            invalidateApiCache('user_dashboard_data');
            invalidateApiCache('fields_preferences');
        })
        .catch(console.error);
  };

  useEffect(() => {
    let timer;
    if (step === STEP.PLAN) {
      const calculateTimeLeft = () => {
        const now = new Date();
        const difference = maturaDateTarget - now;
        if (difference > 0) {
             return {
                days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
                minutes: Math.floor((difference / 1000 / 60) % 60),
                seconds: Math.floor((difference / 1000) % 60),
                isTimeUp: false
             };
        } else {
             return { days: 0, hours: 0, minutes: 0, seconds: 0, isTimeUp: true };
        }
      };
      setCountdown(calculateTimeLeft());
      timer = setInterval(() => {
        setCountdown(calculateTimeLeft());
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [step, maturaDateTarget]);

  // --- RENDEROWANIE ZAWARTOŚCI ---

  // Lista celów — ten sam wygląd dla podstawy i rozszerzenia.
  const renderGoalOptions = (profiles, selectedId, onSelect) => (
      <div className="w-full space-y-3">
          {profiles.map((profile) => {
              const isSelected = selectedId === profile.id;
              return (
                <motion.button
                    key={profile.id}
                    onClick={() => onSelect(profile)}
                    whileTap={{ scale: 0.97 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                    className={`w-full flex items-center p-4 rounded-xl border-2 text-left relative overflow-hidden transition-colors duration-150 ${
                        isSelected
                        ? 'border-[#233855] bg-[#233855]/5 text-[#233855]'
                        : 'border-gray-100 bg-white hover:border-gray-200 text-gray-500'
                    }`}
                >
                    <div className={`p-2 rounded-xl mr-4 shrink-0 transition-all duration-200 ${isSelected ? 'bg-[#233855] text-[#F8AB3B]' : 'bg-gray-100 text-gray-400'}`}>
                        <profile.Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm">{profile.label}</div>
                        <div className="text-xs opacity-70 mt-0.5">{profile.desc}</div>
                    </div>
                    <div className="ml-3 shrink-0 w-6 h-6 flex items-center justify-center">
                        <AnimatePresence mode="wait">
                            {isSelected ? (
                                <motion.div
                                    key="check"
                                    initial={{ scale: 0, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    exit={{ scale: 0, opacity: 0 }}
                                    transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                                    className="w-6 h-6 rounded-full bg-[#233855] flex items-center justify-center"
                                >
                                    <FiCheck className="text-white" size={13} />
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="circle"
                                    initial={{ scale: 0, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    exit={{ scale: 0, opacity: 0 }}
                                    className="w-6 h-6 rounded-full border-2 border-gray-200"
                                />
                            )}
                        </AnimatePresence>
                    </div>
                </motion.button>
              );
          })}
      </div>
  );

  const renderContent = () => {
      switch (step) {

          // --- KROK 0: CO ZDAJESZ ---
          case STEP.EXAM:
              return (
                  <div className="flex flex-col items-center w-full px-2">
                      <p className="text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">Start</p>
                      <h3 className="text-2xl font-apotek uppercase tracking-widest font-bold text-[#233855] mb-5 text-center">
                          Co zdajesz w maju?
                      </h3>
                      <div className="w-full space-y-3">
                          {EXAM_CHOICES.map((choice) => {
                              const isSelected = examChoice === choice.id;
                              return (
                                <motion.button
                                    key={choice.id}
                                    onClick={() => handleExamChoiceSelect(choice.id)}
                                    whileTap={{ scale: 0.97 }}
                                    transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                                    className={`w-full flex items-center p-4 rounded-xl border-2 text-left relative overflow-hidden transition-colors duration-150 ${
                                        isSelected
                                        ? 'border-[#233855] bg-[#233855]/5 text-[#233855]'
                                        : 'border-gray-100 bg-white hover:border-gray-200 text-gray-500'
                                    }`}
                                >
                                    <div className={`p-2 rounded-xl mr-4 shrink-0 transition-all duration-200 ${isSelected ? 'bg-[#233855] text-[#F8AB3B]' : 'bg-gray-100 text-gray-400'}`}>
                                        <choice.Icon className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-bold text-sm">{choice.label}</div>
                                        <div className="text-xs opacity-70 mt-0.5">{choice.desc}</div>
                                    </div>
                                    <div className="ml-3 shrink-0 w-6 h-6 flex items-center justify-center">
                                        <AnimatePresence mode="wait">
                                            {isSelected ? (
                                                <motion.div
                                                    key="check"
                                                    initial={{ scale: 0, opacity: 0 }}
                                                    animate={{ scale: 1, opacity: 1 }}
                                                    exit={{ scale: 0, opacity: 0 }}
                                                    transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                                                    className="w-6 h-6 rounded-full bg-[#233855] flex items-center justify-center"
                                                >
                                                    <FiCheck className="text-white" size={13} />
                                                </motion.div>
                                            ) : (
                                                <motion.div
                                                    key="circle"
                                                    initial={{ scale: 0, opacity: 0 }}
                                                    animate={{ scale: 1, opacity: 1 }}
                                                    exit={{ scale: 0, opacity: 0 }}
                                                    className="w-6 h-6 rounded-full border-2 border-gray-200"
                                                />
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </motion.button>
                              );
                          })}
                      </div>
                  </div>
              );

          // --- KROK 1: CEL Z PODSTAWY (tylko dla "tylko podstawa") ---
          case STEP.GOAL_PP:
              return (
                  <div className="flex flex-col items-center w-full px-2">
                      <p className="text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">Twój cel</p>
                      <h3 className="text-2xl font-apotek uppercase tracking-widest font-bold text-[#233855] mb-5 text-center">
                          Jaki wynik?
                      </h3>
                      {renderGoalOptions(GOAL_PROFILES, selectedGoalProfile.id, setSelectedGoalProfile)}
                  </div>
              );

          // --- KROK 2: CEL Z ROZSZERZENIA (tylko P+R — o podstawę nie pytamy) ---
          case STEP.GOAL_PR:
              return (
                  <div className="flex flex-col items-center w-full px-2">
                      <p className="text-xs uppercase tracking-widest text-gray-400 font-bold mb-2">Twój cel</p>
                      <h3 className="text-2xl font-apotek uppercase tracking-widest font-bold text-[#233855] mb-5 text-center">
                          Jaki wynik z rozszerzenia?
                      </h3>

                      {renderGoalOptions(GOAL_PROFILES_PR, selectedGoalProfilePr.id, setSelectedGoalProfilePr)}

                      <p className="text-xs text-gray-400 text-center mt-4 leading-relaxed max-w-[280px]">
                          Wynik z podstawy oczywiście jak najlepszy.
                      </p>
                  </div>
              );


          // --- KROK 3: LOADING ---
          case STEP.LOADING:
              return (
                  <div className="flex flex-col items-center justify-center h-full text-center px-6">
                      <div className="relative w-24 h-24 mb-6">
                          <div className="absolute inset-0 border-4 border-gray-100 rounded-full"></div>
                          <div className="absolute inset-0 border-4 border-[#E09F2E] rounded-full border-t-transparent animate-spin"></div>
                          <FiCpu className="absolute inset-0 m-auto text-[#233855] w-10 h-10 animate-pulse" />
                      </div>
                      <h3 className="text-lg font-bold text-[#233855] mb-2 font-apotek uppercase tracking-wider animate-pulse">
                          Personalizacja...
                      </h3>
                      <AnimatePresence mode="wait">
                          <motion.p
                              key={processingMsgIndex}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              className="text-sm text-gray-500 min-h-[20px] font-medium"
                          >
                              {PROCESSING_MESSAGES[processingMsgIndex]}
                          </motion.p>
                      </AnimatePresence>
                  </div>
              );

          case STEP.PLAN:
            return (
              <div className="flex flex-col w-full relative">

                <div className="absolute top-0 right-0 w-40 h-40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none opacity-[0.04] bg-[#F8AB3B]" />

                <p className="relative text-xs uppercase tracking-widest text-gray-400 font-bold mb-2 text-center">Twój plan</p>
                <h3 className="relative text-4xl font-apotek uppercase tracking-widest font-bold text-[#233855] leading-tight mb-6 text-center">
                  {finalPlan.strategyName}
                </h3>

                <div className="relative w-full rounded-xl border border-gray-200 shadow-[0_8px_30px_-5px_rgba(35,56,85,0.15)] overflow-hidden bg-white">

                  <div className="flex items-center gap-3 p-4">
                    <div className="p-2.5 rounded-xl bg-[#233855] text-[#F8AB3B] shadow-md shrink-0">
                      {isDual
                        ? <selectedGoalProfilePr.Icon className="w-5 h-5" />
                        : <selectedGoalProfile.Icon className="w-5 h-5" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs uppercase tracking-widest text-gray-400 font-bold mb-0.5">
                        {isDual ? 'Podstawa + Rozszerzenie' : 'Twój cel maturalny'}
                      </p>
                      <p className="text-xl font-bold font-apotek tracking-wide text-[#233855] leading-none">
                        {isDual ? selectedGoalProfilePr.label : selectedGoalProfile.label}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start border-t border-gray-100 px-2 pt-3 pb-3">

                    <div className="flex-1 flex flex-col items-center gap-1 px-1">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#E09F2E] shadow-[0_0_12px_rgba(224,159,46,0.4)]">
                        <FiTarget className="text-white" size={20} />
                      </div>
                      <p className="text-xs font-semibold text-[#C47D00] leading-tight text-center">{finalPlan.dailyTasks} {finalPlan.dailyTasks === 5 ? 'zadań' : 'zadania'} dziennie</p>
                    </div>

                    <div className="w-px bg-gray-100 self-stretch" />

                    <div className="flex-1 flex flex-col items-center gap-1 px-1">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#233855]">
                        <FiTrendingUp className="text-[#E09F2E]" size={20} />
                      </div>
                      {isDual ? (
                        <div className="text-center leading-tight">
                          <p className="text-xs font-semibold text-gray-500">PP {selectedGoalProfile.targetScore}</p>
                          <p className="text-xs font-semibold text-gray-500">PR {selectedGoalProfilePr.targetScore}</p>
                        </div>
                      ) : (
                        <p className="text-xs font-semibold text-gray-500 leading-tight text-center">Cel: {selectedGoalProfile.targetScore}</p>
                      )}
                    </div>

                    <div className="w-px bg-gray-100 self-stretch" />

                    <div className="flex-1 flex flex-col items-center gap-1 px-1">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#233855]">
                        <FiCalendar className="text-[#E09F2E]" size={20} />
                      </div>
                      <p className="text-xs font-semibold text-gray-500 leading-tight text-center">{countdown?.days ?? '...'} dni do matury</p>
                    </div>

                  </div>

                </div>

                <p className="relative text-xs text-gray-400 text-center mt-3 leading-relaxed">
                  {isDual
                    ? 'PP/PR, cel maturalny i dzienny możesz zmieniać w dowolnym momencie. '
                    : 'Cel maturalny i dzienny możesz zmieniać w dowolnym momencie.'}
                </p>
              </div>
            );

          case STEP.PUSH: {
            // Mockup = pierwszy wariant prawdziwego pusha dla wybranego celu (notifications_config,
            // warianty po onboarding_goal_id; przy PP+PR cel podstawy to zawsze "max").
            const tasks = finalPlan.dailyTasks;
            const tasksWord = tasks === 1 ? 'zadanie' : (tasks >= 2 && tasks <= 4) ? 'zadania' : 'zadań';
            const pushByGoal = {
                max:   { title: '🔥 Krok do wymarzonych studiów', body: 'bliżej wymarzonego kierunku' },
                solid: { title: '🔥 Krok do Twojego celu',        body: 'bliżej solidnego wyniku' },
                pass:  { title: '🙂 Krok do spokoju na maturze',  body: 'bliżej spokoju na maturze' },
            };
            const push = pushByGoal[selectedGoalProfile.id] || pushByGoal.solid;
            const goalText = isDual
                ? `${selectedGoalProfilePr.targetScore} z rozszerzenia`
                : `${selectedGoalProfile.targetScore} na maturze`;
            return (
              <div className="flex flex-col items-center w-full text-center">
                <div className="w-14 h-14 rounded-2xl bg-[#233855] flex items-center justify-center mb-4 shadow-[0_6px_20px_-4px_rgba(35,56,85,0.6)]">
                  <FiBell size={26} style={{ color: '#F8AB3B' }} />
                </div>
                <h3 className="text-2xl font-apotek uppercase tracking-widest font-bold text-[#233855] mb-2 leading-tight">
                  Pamiętamy o nauce za Ciebie
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed max-w-[290px] mb-5">
                  Raz dziennie przypomnimy Ci o Twoim celu: <b className="text-[#233855]">{goalText}</b>. Ty się uczysz, my pilnujemy kiedy.
                </p>
                <div className="w-full max-w-[290px] bg-white rounded-xl shadow-[0_4px_20px_-5px_rgba(35,56,85,0.15)] border border-gray-200 p-4 text-left">
                  <div className="flex items-center gap-2 mb-1.5">
                    <img src={matematlyLogo} alt="" className="w-6 h-6" />
                    <span className="text-xs font-bold text-[#233855] uppercase tracking-wide">Matematly</span>
                  </div>
                  <p className="text-sm font-bold text-[#233855] leading-tight">{push.title}</p>
                  <p className="text-xs text-gray-600 leading-snug">Zrób {tasks} {tasksWord} dziś - {push.body}</p>
                </div>
                <button
                  type="button"
                  onClick={handlePushAccept}
                  disabled={pushBusy}
                  className="w-full mt-6 rounded-lg px-4 py-4 text-sm font-apotek uppercase tracking-widest text-slate-100 shadow-md transition-transform active:scale-95 bg-[#233855] hover:bg-[#345081] disabled:opacity-60"
                >
                  Tak, przypominaj mi
                </button>
                <button
                  type="button"
                  onClick={handlePushDecline}
                  className="mt-3 px-4 py-2 text-sm text-gray-400 underline underline-offset-2 hover:text-gray-600 transition-colors"
                >
                  Nie teraz
                </button>
              </div>
            );
          }
          case STEP.TRIAL: {
            // Kafelki funkcji usunięte (klikało je <5%) — miejsce dostała nagroda za pierwszy cel.
            const goalTasks = finalPlan.dailyTasks;
            const goalTasksWord = goalTasks === 1 ? 'zadanie' : (goalTasks >= 2 && goalTasks <= 4) ? 'zadania' : 'zadań';
            return (
              <div className="flex flex-col items-center w-full relative">

                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-56 h-32 bg-[#F8AB3B] opacity-[0.07] blur-3xl pointer-events-none rounded-full" />

                <div className="relative text-center w-full mb-5">
                  <p className="text-sm uppercase tracking-widest text-gray-400 font-bold mb-3">
                    na start dostajesz
                  </p>
                  <div className="flex items-center justify-center gap-3 mb-4">
                    <span className="text-8xl font-bold text-[#233855] font-apotek leading-none">3</span>
                    <div className="text-left">
                      <span className="block text-3xl font-bold text-[#233855] font-apotek leading-tight">dni dostępu</span>
                      <span className="block text-3xl font-bold text-[#E09F2E] font-apotek leading-tight">za darmo</span>
                    </div>
                  </div>
                  <p className="text-base font-bold text-[#233855] mb-1.5 [text-wrap:balance]">
                    Płacisz tylko jeśli zdecydujesz, że warto.
                  </p>
                  <p className="text-sm text-gray-500 leading-relaxed max-w-[280px] mx-auto [text-wrap:balance]">
                    Pełny dostęp do wszystkiego. Bez karty. Bez zobowiązań. Bez subskrypcji.
                  </p>
                </div>

                <p className="relative text-xs text-gray-500 text-center leading-relaxed max-w-[260px] [text-wrap:balance]">
                  Zrób dziś <b className="text-[#233855]">{goalTasks} {goalTasksWord}</b>, a odblokujesz cenę dla ambitnych, ważną do końca jutrzejszego dnia.
                </p>
              </div>
            );
          }
          default: return null;
      }
  };

  // Kropki tylko dla kroków, w których user coś robi (loading pomijamy).
  const dotsMapping = [
      STEP.EXAM,
      isDual ? STEP.GOAL_PR : STEP.GOAL_PP,
      STEP.PLAN,
      ...(hasPushStep ? [STEP.PUSH] : []),
      STEP.TRIAL,
  ];

  return ReactDOM.createPortal(
    <motion.div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9998] flex items-center justify-center p-4"
      variants={backdropVariants}
      initial="hidden"
      animate="visible"
      exit="hidden"
    >
      <motion.div
        className="bg-white rounded-xl lg:rounded-2xl shadow-[0_8px_30px_-5px_rgba(35,56,85,0.15)] w-full max-w-md mx-auto overflow-hidden relative flex flex-col max-h-[85vh] h-auto"
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={(e) => e.stopPropagation()}
        style={{ minHeight: 'auto' }}
      >
        <div className="relative p-6 pt-8 flex flex-col justify-center flex-grow overflow-y-auto overflow-x-hidden min-h-[400px]">
            <AnimatePresence mode="wait">
                <motion.div
                    key={step}
                    variants={contentVariants}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    className="w-full flex flex-col justify-center"
                >
                    {renderContent()}
                </motion.div>
            </AnimatePresence>
        </div>

        {step !== STEP.LOADING && (
            <div className="p-6 bg-gray-50/50 flex flex-col gap-3 border-t border-gray-100 mt-auto shrink-0">
                    <div className="flex items-center justify-between w-full">
                        <div className="flex gap-2">
                            {dotsMapping.map((s) => (
                                <div
                                    key={s}
                                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                                        step >= s ? 'bg-[#233855]' : 'bg-gray-300'
                                    }`}
                                />
                            ))}
                        </div>

                        {/* Na kroku pushy przyciski są w treści (duży + "Nie teraz" pod nim) — w stopce same kropki. */}
                        {step !== STEP.PUSH && (
                        <div className="flex gap-3">
                            {step > STEP.EXAM && (
                                <button
                                    type="button"
                                    onClick={handleBack}
                                    className="rounded-lg px-4 py-3 text-sm font-apotek uppercase tracking-widest text-gray-600 hover:bg-gray-100 transition-colors"
                                >
                                    Wstecz
                                </button>
                            )}
                            
                            <button
                                type="button"
                                onClick={handleNext}
                                disabled={pushBusy}
                                className={`flex justify-center rounded-lg px-4 py-3 text-sm font-apotek uppercase tracking-widest text-slate-100 shadow-md transition-transform active:scale-95 bg-[#233855] hover:bg-[#345081] disabled:opacity-60`}
                            >
                                {step === STEP.TRIAL ? 'Zaczynajmy!' : 'Dalej'}
                            </button>
                        </div>
                        )}
                    </div>
            </div>
        )}
      </motion.div>
    </motion.div>,
    document.body
  );
};

export default OnboardingPopup;